/**
 * Calibration Procedures Page
 * Main page for calibration procedure CRUD operations
 */

import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Plus, RefreshCw, Search } from 'lucide-react';
import { useDebounce } from '@/hooks/useDebounce';
import { useCalibrationProceduresList, useDeleteCalibrationProcedure } from '../hooks';
import { CalibrationProcedureTable } from '../components/CalibrationProcedureTable';
import { CalibrationProcedureModal } from '../components/CalibrationProcedureModal';
import type { CalibrationProcedure, CalibrationProcedureFilters } from '../types';

// =============================================================================
// Page Component
// =============================================================================

export default function CalibrationProceduresPage() {
  // ---------------------------------------------------------------------------
  // Filter state
  // ---------------------------------------------------------------------------
  const [search, setSearch] = useState('');
  const [deletedFilter, setDeletedFilter] = useState<string>('false');
  const debouncedSearch = useDebounce(search, 300);

  // ---------------------------------------------------------------------------
  // Sort state
  // ---------------------------------------------------------------------------
  const [sortKey, setSortKey] = useState<string>('name');
  const [sortDirection, setSortDirection] = useState<number>(1);

  // ---------------------------------------------------------------------------
  // Pagination
  // ---------------------------------------------------------------------------
  const [page, setPage] = useState(0);
  const pageLength = 25;

  // Reset page when filters change
  useEffect(() => {
    setPage(0);
  }, [debouncedSearch, deletedFilter, sortKey, sortDirection]);

  // ---------------------------------------------------------------------------
  // Modal state
  // ---------------------------------------------------------------------------
  const [modalOpen, setModalOpen] = useState(false);
  const [editingProcedure, setEditingProcedure] = useState<
    CalibrationProcedure | undefined
  >(undefined);

  // ---------------------------------------------------------------------------
  // Delete confirmation state
  // ---------------------------------------------------------------------------
  const [deleteTarget, setDeleteTarget] = useState<CalibrationProcedure | null>(null);
  const deleteMutation = useDeleteCalibrationProcedure();

  // ---------------------------------------------------------------------------
  // API query
  // ---------------------------------------------------------------------------
  const filters: CalibrationProcedureFilters = {
    search: debouncedSearch || undefined,
    deleted: deletedFilter !== 'all' ? deletedFilter : undefined,
    page,
    pageLength,
    sortKey,
    sortDirection,
  };

  const proceduresQuery = useCalibrationProceduresList(filters);
  const procedures = proceduresQuery.data?.calibrationProcedures || [];
  const total = proceduresQuery.data?.total || 0;

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------
  const handleEdit = (procedure: CalibrationProcedure) => {
    setEditingProcedure(procedure);
    setModalOpen(true);
  };

  const handleCreate = () => {
    setEditingProcedure(undefined);
    setModalOpen(true);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    await deleteMutation.mutateAsync(deleteTarget.mongoProcedureId);
    setDeleteTarget(null);
  };

  const handleSortChange = (key: string, direction: number) => {
    setSortKey(key);
    setSortDirection(direction);
  };

  const handleRefresh = () => {
    proceduresQuery.refetch();
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <div className="space-y-5">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="page-title">
            Calibration Procedures{' '}
            <small>Calibration procedures management</small>
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={handleRefresh}
            disabled={proceduresQuery.isFetching}
          >
            <RefreshCw
              className={`h-4 w-4 mr-2 ${proceduresQuery.isFetching ? 'animate-spin' : ''}`}
            />
            Refresh
          </Button>
          <Button onClick={handleCreate}>
            <Plus className="h-4 w-4 mr-2" />
            Create Procedure
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search procedures..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={deletedFilter} onValueChange={setDeletedFilter}>
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="false">Enabled</SelectItem>
            <SelectItem value="true">Deleted</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      {proceduresQuery.error ? (
        <Card>
          <CardContent className="py-8 text-center">
            <p className="text-red-500">
              Error loading calibration procedures. Please try again.
            </p>
            <Button
              variant="outline"
              className="mt-4"
              onClick={handleRefresh}
            >
              Retry
            </Button>
          </CardContent>
        </Card>
      ) : (
        <CalibrationProcedureTable
          procedures={procedures}
          total={total}
          page={page}
          pageLength={pageLength}
          sortKey={sortKey}
          sortDirection={sortDirection}
          onPageChange={setPage}
          onSortChange={handleSortChange}
          onEdit={handleEdit}
          onDelete={setDeleteTarget}
          isLoading={proceduresQuery.isLoading}
        />
      )}

      {/* Create/Edit Modal */}
      <CalibrationProcedureModal
        open={modalOpen}
        onOpenChange={(open) => {
          setModalOpen(open);
          if (!open) setEditingProcedure(undefined);
        }}
        procedure={editingProcedure}
      />

      {/* Delete Confirmation */}
      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Calibration Procedure</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &quot;{deleteTarget?.name}&quot;?
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
